import { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TuneIcon from '@mui/icons-material/Tune';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useFilterStore } from '../../stores/filterStore';

const currentYear = dayjs().year();
const YEARS = Array.from({ length: 15 }, (_, i) => currentYear - i);

const MONTHS = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];

const ADVANCED_PRESETS = [
  { label: 'חודש אחרון', from: () => dayjs().subtract(1, 'month').startOf('month'), to: () => dayjs().subtract(1, 'month').endOf('month') },
  { label: 'רבעון אחרון', from: () => dayjs().subtract(3, 'month').startOf('month'), to: () => dayjs().subtract(1, 'month').endOf('month') },
  { label: 'שנתיים', from: () => dayjs().subtract(2, 'year').startOf('year'), to: () => dayjs() },
  { label: '5 שנים', from: () => dayjs().subtract(5, 'year').startOf('year'), to: () => dayjs() },
  { label: 'הכל', from: () => dayjs('2010-01-01'), to: () => dayjs() },
];

export default function DateRangePicker() {
  const { fromDate, toDate, setDateRange } = useFilterStore();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [monthAnchor, setMonthAnchor] = useState<null | HTMLElement>(null);
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);

  const handleYear = (year: number) => {
    setDateRange(
      dayjs().year(year).startOf('year'),
      dayjs().year(year).endOf('year'),
    );
    setSelectedMonths([]);
  };

  const handleMonthToggle = useCallback((monthIndex: number) => {
    setSelectedMonths((prev) => {
      const next = prev.includes(monthIndex)
        ? prev.filter((m) => m !== monthIndex)
        : [...prev, monthIndex].sort((a, b) => a - b);
      return next;
    });
  }, []);

  const handleMonthApply = () => {
    if (selectedMonths.length === 0) {
      setMonthAnchor(null);
      return;
    }
    const year = fromDate.year();
    const minMonth = Math.min(...selectedMonths);
    const maxMonth = Math.max(...selectedMonths);
    setDateRange(
      dayjs().year(year).month(minMonth).startOf('month'),
      dayjs().year(year).month(maxMonth).endOf('month'),
    );
    setMonthAnchor(null);
  };

  const handleMonthOpen = (e: React.MouseEvent<HTMLElement>) => {
    // Pre-select current months if a month range is active
    const sameYear = fromDate.year() === toDate.year();
    if (sameYear && !(fromDate.month() === 0 && fromDate.date() === 1 && toDate.month() === 11 && toDate.date() === 31)) {
      const months: number[] = [];
      for (let m = fromDate.month(); m <= toDate.month(); m++) months.push(m);
      setSelectedMonths(months);
    } else {
      setSelectedMonths([]);
    }
    setMonthAnchor(e.currentTarget);
  };

  // Detect which year is currently selected (for highlighting)
  const selectedYear = fromDate.month() === 0 && fromDate.date() === 1
    && toDate.month() === 11 && toDate.date() === 31
    && fromDate.year() === toDate.year()
    ? fromDate.year()
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Primary row: fiscal year buttons */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <ButtonGroup size="small" variant="outlined" sx={{ '& .MuiButton-root': { fontSize: '0.8rem', px: 1.5, minWidth: 55 } }}>
          {YEARS.slice(0, 6).map((year) => (
            <Button
              key={year}
              onClick={() => handleYear(year)}
              variant={selectedYear === year ? 'contained' : 'outlined'}
            >
              {year}
            </Button>
          ))}
          <Button onClick={handleMonthOpen}>
            חודש ▾
          </Button>
        </ButtonGroup>

        <Popover
          open={Boolean(monthAnchor)}
          anchorEl={monthAnchor}
          onClose={() => setMonthAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Box sx={{ p: 2, width: 320 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              בחר חודשים — {fromDate.year()}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              {MONTHS.map((name, idx) => (
                <Chip
                  key={idx}
                  label={name}
                  size="small"
                  onClick={() => handleMonthToggle(idx)}
                  color={selectedMonths.includes(idx) ? 'primary' : 'default'}
                  variant={selectedMonths.includes(idx) ? 'filled' : 'outlined'}
                  sx={{ minWidth: 70 }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button size="small" onClick={() => setSelectedMonths([])} disabled={selectedMonths.length === 0}>נקה</Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" onClick={() => setMonthAnchor(null)}>ביטול</Button>
              <Button size="small" variant="contained" onClick={handleMonthApply} disabled={selectedMonths.length === 0}>
                החל ({selectedMonths.length})
              </Button>
            </Box>
          </Box>
        </Popover>

        <Tooltip title="אפשרויות מתקדמות">
          <IconButton
            size="small"
            onClick={() => setShowAdvanced(!showAdvanced)}
            color={showAdvanced ? 'primary' : 'default'}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Advanced row: presets + date pickers */}
      <Collapse in={showAdvanced}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <ButtonGroup size="small" variant="outlined" sx={{ '& .MuiButton-root': { fontSize: '0.72rem', px: 1 } }}>
            {ADVANCED_PRESETS.map((p) => (
              <Button key={p.label} onClick={() => setDateRange(p.from(), p.to())}>
                {p.label}
              </Button>
            ))}
          </ButtonGroup>

          <DatePicker
            label="מתאריך"
            value={fromDate}
            onChange={(date) => { if (date) setDateRange(date, toDate); }}
            slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
          />
          <DatePicker
            label="עד תאריך"
            value={toDate}
            onChange={(date) => { if (date) setDateRange(fromDate, date); }}
            slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
